import React from 'react';
import { useTranslation } from 'react-i18next';
import { Filter, Search, X, Download, FileText, FileSpreadsheet, FileDown, Check, CheckCircle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuSeparator,
} from '../ui/dropdown-menu';

const PayrollFiltersCard = ({
  searchTerm,
  setSearchTerm,
  statusFilter,
  setStatusFilter,
  departmentFilter,
  setDepartmentFilter,
  departments,
  filteredEntries,
  entries,
  selectedEntries,
  setSelectedEntries,
  bulkActionLoading,
  handleBulkExport,
  handleExportExcel,
  handleBulkPDFGeneration,
  handleBulkApprove,
}) => {
  const { t } = useTranslation();
  return (
  <div className="space-y-6">
    <Card className="bg-card border-border">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Filter className="h-5 w-5 text-blue-600" />
          <span className="text-foreground">{t('payrollUi.filters.title')}</span>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder={t('payrollUi.filters.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-9 bg-card"
            />
            {searchTerm && (
              <Button
                variant="ghost"
                size="sm"
                className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                onClick={() => setSearchTerm('')}
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>

          <div className="flex gap-2">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 bg-card">
                <SelectValue placeholder={t('payrollUi.filters.statusPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('payrollUi.filters.all')}</SelectItem>
                <SelectItem value="approved">{t('payrollUi.filters.approved')}</SelectItem>
                <SelectItem value="pending">{t('payrollUi.filters.pending')}</SelectItem>
              </SelectContent>
            </Select>

            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="w-40 bg-card">
                <SelectValue placeholder={t('payrollUi.filters.deptPlaceholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('payrollUi.filters.all')}</SelectItem>
                {departments.map((dept) => (
                  <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="outline"
                  disabled={bulkActionLoading || filteredEntries.length === 0}
                  className="bg-card"
                >
                  <Download className="h-4 w-4 mr-2" />
                  {t('payrollUi.filters.export')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuItem onClick={() => handleBulkExport()}>
                  <FileText className="h-4 w-4 mr-2" />
                  {t('payrollUi.filters.payslipsCsv')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExportExcel()}>
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  {t('payrollUi.filters.excel')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleBulkPDFGeneration}
                  className="bg-red-950/20 text-red-400 font-semibold"
                >
                  <FileDown className="h-4 w-4 mr-2 text-red-400" />
                  {t('payrollUi.filters.payslipsPdfBatch')}
                  <span className="ml-auto text-xs bg-red-900/40 text-red-300 px-2 py-1 rounded-full">TN</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
          <span>{t('payrollUi.filters.resultsCount', { filtered: filteredEntries.length, total: entries.length })}</span>
          {selectedEntries.length > 0 && (
            <span className="text-blue-600 font-medium">{t('payrollUi.filters.selectedCount', { count: selectedEntries.length })}</span>
          )}
        </div>
      </CardContent>
    </Card>

    {selectedEntries.length > 0 && (
      <Card className="border-primary/30 bg-primary/5">
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckCircle className="h-5 w-5 text-blue-600" />
              <span className="font-semibold text-foreground">
                {t('payrollUi.filters.selectedPayslips', { count: selectedEntries.length })}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                onClick={handleBulkApprove}
                disabled={bulkActionLoading}
                className="bg-green-600 hover:bg-green-700"
              >
                <Check className="h-4 w-4 mr-1" />
                {t('payrollUi.filters.approve')}
              </Button>
              <Button
                size="sm"
                onClick={handleBulkPDFGeneration}
                disabled={bulkActionLoading}
                className="text-white"
                style={{ backgroundColor: "#e5484d" }}
              >
                <FileDown className="h-4 w-4 mr-1" />
                PDF 🇹🇳
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedEntries([])}
                className="border-border"
              >
                {t('common.cancel')}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    )}
  </div>
  );
};

export default PayrollFiltersCard;
